import { PlusIcon } from "lucide-react";
import { useState } from "react";

import { Button } from "@/web/components/primitives/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/web/components/primitives/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/web/components/primitives/tabs";
import { toaster } from "@/web/components/primitives/toast";
import { formatCanonicalAddressMedium, formatCanonicalAddressShort } from "@/web/lib/addresses";
import { ErrorHandler } from "@/web/lib/errors";
import { formatCompactWholeNumberWithUnit, formatNumberToShort } from "@/web/lib/formatting";
import { useProcedureMutation, useReducerMutation } from "@/web/lib/tanstack";
import { procedures, reducers } from "@tycho/web-bindings";
import type { UnderwritableEntitiesV4 } from "@tycho/web-bindings/types";
import {
  AcknowledgmentTooltip,
  AcknowledgmentTooltipStep,
} from "@/web/routes/_protected/smb/-dashboard/-acknowledgments/AcknowledgmentTooltip";
import { MESSAGE_KEYS } from "@/web/routes/_protected/smb/-dashboard/-acknowledgments/messageKeys";
import { useTourStep } from "@/web/routes/_protected/smb/-dashboard/-acknowledgments/useTourStep";
import {
  ExplorePropertiesDrawer,
  MAX_SELECTIONS,
} from "@/web/routes/_protected/smb/-dashboard/-actions/-ExplorePropertiesDrawer";
import {
  GenericFooterDrawer,
  GenericFooterDrawerTrigger,
  GenericFooterTileLoader,
} from "@/web/routes/_protected/smb/-dashboard/-footer/-GenericFooterDrawer";
import { NegotiateValuationChart } from "@/web/routes/_protected/smb/-dashboard/-footer/-NegotiateValuationChart";
import {
  averageLastSalePriceFromComps,
  resolvePriceEstimateNumber,
} from "@/web/routes/_protected/smb/-dashboard/comps";
import { useSuspendedSmbSuperView } from "@/web/routes/_protected/smb/-dashboard/superView";

type SuperView = ReturnType<typeof useSuspendedSmbSuperView>;
type TargetedProperty = SuperView["targetedProperties"][number];

export function NegotiateTileButton({
  underwritableEntityId,
}: {
  underwritableEntityId: UnderwritableEntitiesV4["id"];
}) {
  return (
    <GenericFooterTileLoader>
      <SuspendedNegotiateTileButton underwritableEntityId={underwritableEntityId} />
    </GenericFooterTileLoader>
  );
}

/**
 * Headline price for a single targeted property: our explicit valuation when we
 * have one, otherwise the comps-derived average. Mirrors the precedence every
 * other single-price surface uses (`resolvePriceEstimateNumber`), so a seeded
 * valuation and the comps estimate never disagree across the dashboard. A
 * nullish `priceEstimate` changes nothing.
 */
function estimateForProperty(property: TargetedProperty): number | undefined {
  // `priceEstimate` is held out of smb_super_view for now (see superView.ts).
  return resolvePriceEstimateNumber(property.targetableProperty, property.topThreeComps ?? []);
}

function formatEstimate(estimate: number | undefined): string {
  return estimate
    ? formatCompactWholeNumberWithUnit(formatNumberToShort(estimate), "Dollars")
    : "TBD";
}

function SuspendedNegotiateTileButton({
  underwritableEntityId,
}: {
  underwritableEntityId: UnderwritableEntitiesV4["id"];
}) {
  const [isOpen, setIsOpen] = useState(false);
  const tourRef = useTourStep("negotiate");
  const { targetedProperties, appraisals } = useSuspendedSmbSuperView(underwritableEntityId);

  // Headline summarizes the estimates across every targeted property:
  //   - no priced property → "TBD"
  //   - exactly one priced  → that price
  //   - multiple priced      → "${min}+" (the cheapest, since a deal can start there)
  const pricedEstimates = targetedProperties
    .map(estimateForProperty)
    .filter((e): e is number => e !== undefined && e > 0);
  const title =
    pricedEstimates.length === 0
      ? "TBD"
      : pricedEstimates.length === 1
        ? formatEstimate(pricedEstimates[0])
        : `${formatEstimate(Math.min(...pricedEstimates))}+`;
  // Mirrors `propertyHasReviewedComps` from the rules engine — the user has
  // approved at least one comp on any targeted property, surfaced as
  // `topThreeComps`.
  const hasReviewedComps = targetedProperties.some((p) => (p.topThreeComps?.length ?? 0) > 0);

  return (
    <>
      <AcknowledgmentTooltip side="top" color="red">
        <AcknowledgmentTooltipStep
          messageKey={MESSAGE_KEYS.negotiateReviewedComps}
          when={hasReviewedComps}
          title="Comps reviewed"
          body="We calculated a baseline purchase price based on comparable sales in the area."
        />
        <GenericFooterDrawerTrigger
          ref={tourRef}
          title={title}
          subtitle="Price"
          color="red"
          onClick={() => setIsOpen(true)}
        />
      </AcknowledgmentTooltip>
      <GenericFooterDrawer title="Price" isOpen={isOpen} setIsOpen={setIsOpen}>
        <NegotiateDrawerContent
          underwritableEntityId={underwritableEntityId}
          targetedProperties={targetedProperties}
          appraisals={appraisals}
        />
      </GenericFooterDrawer>
    </>
  );
}

function NegotiateDrawerContent({
  underwritableEntityId,
  targetedProperties,
  appraisals,
}: {
  underwritableEntityId: UnderwritableEntitiesV4["id"];
  targetedProperties: SuperView["targetedProperties"];
  appraisals: SuperView["appraisals"];
}) {
  const [exploreOpen, setExploreOpen] = useState(false);

  const activeCount = targetedProperties.length;
  const canAdd = activeCount < MAX_SELECTIONS;
  const canRemove = activeCount > 1;

  // Controlled tab selection keyed by targetable-property id. Radix does NOT
  // auto-recover a controlled value that no longer matches a trigger, so when
  // the active property is removed we reconcile to the first remaining id during
  // render (rather than rendering an empty panel). A freshly-added property just
  // appears as a new tab; the active one stays put.
  const ids = targetedProperties.map((p) => p.targetableProperty.id.toString());
  const [activeTab, setActiveTab] = useState(() => ids[0] ?? "");
  const resolvedTab = ids.includes(activeTab) ? activeTab : (ids[0] ?? "");

  // Already-targeted property ids — passed to the Explore drawer so it only
  // offers *new* picks and caps at the remaining slots.
  const targetedIds = new Set(ids);

  return (
    <>
      <Tabs value={resolvedTab} onValueChange={setActiveTab} className="gap-6">
        {/*
          Text-only tab strip (per Figma): active address in red + underline,
          the rest muted. Override the primitive's default boxed/dark-blue look.
        */}
        <TabsList className="h-auto w-full justify-start gap-6 overflow-x-auto border-0 bg-transparent p-0">
          {targetedProperties.map((property) => (
            <TabsTrigger
              key={property.targetableProperty.id.toString()}
              value={property.targetableProperty.id.toString()}
              className="h-auto flex-none border-b-2 border-transparent px-0 py-1 text-base text-withus-red/40 underline-offset-4 data-[state=active]:border-withus-red data-[state=active]:bg-transparent data-[state=active]:text-withus-red"
            >
              {formatCanonicalAddressShort(property.targetableProperty.canonicalAddress)}
            </TabsTrigger>
          ))}
        </TabsList>

        {targetedProperties.map((property) => (
          <TabsContent
            key={property.targetableProperty.id.toString()}
            value={property.targetableProperty.id.toString()}
          >
            <NegotiatePropertySection
              underwritableEntityId={underwritableEntityId}
              property={property}
              appraisals={appraisals}
              canRemove={canRemove}
            />
          </TabsContent>
        ))}

        {canAdd ? (
          <Button
            variant="outline"
            color="red"
            size="lg"
            className="w-full"
            onClick={() => setExploreOpen(true)}
          >
            <PlusIcon className="size-4" />
            Add a property
          </Button>
        ) : null}
      </Tabs>

      <ExplorePropertiesDrawer
        open={exploreOpen}
        underwritableEntity={underwritableEntityId}
        onOpenChange={setExploreOpen}
        onComplete={() => setExploreOpen(false)}
        excludeTargetablePropertyIds={targetedIds}
        maxSelections={MAX_SELECTIONS - activeCount}
      />
    </>
  );
}

function NegotiatePropertySection({
  underwritableEntityId,
  property,
  appraisals,
  canRemove,
}: {
  underwritableEntityId: UnderwritableEntitiesV4["id"];
  property: TargetedProperty;
  appraisals: SuperView["appraisals"];
  canRemove: boolean;
}) {
  const [confirmRemoveOpen, setConfirmRemoveOpen] = useState(false);

  const targetedPropertyId = property.targetableProperty.id;
  const hasAppraisal = appraisals.some(
    (a) => a.targetableProperty.compareTo(targetedPropertyId) === 0,
  );
  const estimate = estimateForProperty(property);
  const title = formatEstimate(estimate);
  const userMaxPrice = property.userMaxPrice;
  // The chart keeps comps and "our valuation" as distinct bars, so it needs the
  // raw comps average rather than the resolved headline figure above.
  const compsEstimate = averageLastSalePriceFromComps(
    property.topThreeComps ?? [],
    property.targetableProperty,
  );

  const { mutateAsync: requestAppraisal, isPending: isRequestingAppraisal } = useProcedureMutation(
    procedures.requestAppraisalForProperty,
    {
      onError: ErrorHandler.default,
      onSuccess: () => {
        toaster({
          message: "Appraisal request sent",
          variant: "success",
          sonnerOptions: { duration: Infinity },
        });
      },
    },
  );

  const { mutate: untargetProperty, isPending: isRemoving } = useReducerMutation(
    reducers.untargetPropertyForUwEntity,
    {
      onError: ErrorHandler.default,
      onSuccess: () => {
        // The soft-deleted property drops out of the subscription-backed views,
        // so this section unmounts; close the (now-orphaned) dialog first.
        setConfirmRemoveOpen(false);
        toaster({ message: "Property removed", variant: "success" });
      },
    },
  );

  const handleRemove = () =>
    untargetProperty({
      underwritableEntity: underwritableEntityId,
      targetableProperty: targetedPropertyId,
    });

  return (
    <div className="flex flex-col gap-4">
      <section className="flex w-full flex-col items-center justify-center gap-4 bg-withus-red p-10 text-center text-withus-white">
        <span className="withus-strong text-4xl">{title}</span>
        {estimate ? (
          <span>
            <p>
              Price estimate for{" "}
              <b>{formatCanonicalAddressMedium(property.targetableProperty.canonicalAddress)}</b>
            </p>
          </span>
        ) : (
          <span>
            <p>
              <b>{formatCanonicalAddressMedium(property.targetableProperty.canonicalAddress)}</b>
            </p>
            We don't have enough data to calculate a price; keep completing actions to gather more
            info
          </span>
        )}
      </section>
      <section>
        <div className="flex min-h-60 w-full items-center justify-center border-2 border-withus-red bg-withus-white px-3 py-4">
          <NegotiateValuationChart
            compsEstimate={compsEstimate}
            ourValuation={undefined}
            userMaxPrice={userMaxPrice}
          />
        </div>
      </section>
      <section>
        <div className="flex items-center justify-between border-2 border-withus-red p-2">
          <p className="line-clamp-2 shrink text-sm text-withus-red">Want a more accurate price?</p>
          <Button
            variant="solid"
            color="red"
            size="sm"
            disabled={hasAppraisal}
            isLoading={isRequestingAppraisal}
            onClick={() =>
              requestAppraisal({
                underwritableEntity: underwritableEntityId,
                targetableProperty: targetedPropertyId,
              })
            }
          >
            Order an Appraisal
          </Button>
        </div>
      </section>
      {canRemove ? (
        <section>
          <Button
            variant="ghost"
            color="red"
            size="sm"
            className="w-full"
            onClick={() => setConfirmRemoveOpen(true)}
          >
            Remove this property
          </Button>
        </section>
      ) : null}

      <Dialog open={confirmRemoveOpen} onOpenChange={setConfirmRemoveOpen}>
        <DialogContent className="max-w-md p-6">
          <DialogHeader>
            <DialogTitle>Remove this property?</DialogTitle>
            <DialogDescription>
              {formatCanonicalAddressMedium(property.targetableProperty.canonicalAddress)} will be
              removed from your targets. Your comps and budget are kept and restored if you add it
              back later.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" color="darkBlue" onClick={() => setConfirmRemoveOpen(false)}>
              Cancel
            </Button>
            <Button variant="solid" color="red" isLoading={isRemoving} onClick={handleRemove}>
              Remove
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
